import { DialogueRewardLevel } from '../api/models';
/**
 * Validates the entire dialogue structure and data integrity
 */
export function validateDialogue(dialogue) {
    const errors = [];
    const warnings = [];
    if (!dialogue.nodes || dialogue.nodes.length === 0) {
        errors.push({
            nodeId: 'dialogue',
            message: 'Dialogue must contain at least one node',
            type: 'error'
        });
        return { isValid: false, errors, warnings };
    }
    // 1. Check root node constraints
    validateRootNode(dialogue, errors);
    // 2. Validate each NPC node
    dialogue.nodes.forEach(node => {
        validateNPCNode(node, errors, warnings);
    });
    // 3. Validate response options
    dialogue.nodes.forEach(node => {
        if (node.responseOptions) {
            node.responseOptions.forEach(response => {
                validateResponseOption(response, node.id, errors, warnings);
                // Validate response connection in dialogue structure
                validateResponseInDialogue(response, node, dialogue, errors);
            });
        }
    });
    // 4. Detect cycles in nextDialogueId graph (nested edges)
    const graph = new Map(); // nodeId -> children npcIds
    dialogue.nodes.forEach(node => {
        (node.responseOptions || []).forEach(resp => {
            if (resp.nextDialogueId) {
                if (!graph.has(node.id))
                    graph.set(node.id, []);
                graph.get(node.id).push(resp.nextDialogueId);
            }
        });
    });
    const visited = new Set();
    const stack = new Set();
    const dfs = (nodeId) => {
        if (stack.has(nodeId))
            return true; // cycle
        if (visited.has(nodeId))
            return false;
        visited.add(nodeId);
        stack.add(nodeId);
        const children = graph.get(nodeId) || [];
        for (const child of children) {
            if (dfs(child))
                return true;
        }
        stack.delete(nodeId);
        return false;
    };
    const hasCycle = dialogue.nodes.some(n => dfs(n.id));
    if (hasCycle) {
        // Mark all responseOptions participating in the cycle as errors
        const markCycleErrors = (currentId, seen) => {
            const children = graph.get(currentId) || [];
            children.forEach(childId => {
                if (seen.has(childId)) {
                    // Find the response(s) that create the cycle
                    dialogue.nodes.forEach(parentNode => {
                        (parentNode.responseOptions || []).forEach(resp => {
                            if (resp.nextDialogueId === childId) {
                                errors.push({
                                    nodeId: `response_${parentNode.id}_${resp.id}`,
                                    message: 'Response creates cyclic dialogue path',
                                    type: 'error'
                                });
                            }
                        });
                    });
                }
                else {
                    seen.add(childId);
                    markCycleErrors(childId, seen);
                }
            });
        };
        markCycleErrors(dialogue.rootNodeId || dialogue.nodes[0].id, new Set([dialogue.rootNodeId]));
    }
    return {
        isValid: errors.length === 0,
        errors,
        warnings
    };
}
/**
 * Validates root node constraints
 */
function validateRootNode(dialogue, errors) {
    const rootNodeId = dialogue.rootNodeId;
    if (!rootNodeId) {
        errors.push({
            nodeId: 'dialogue',
            message: 'Dialogue must have a root node',
            type: 'error'
        });
        return;
    }
    const rootNode = dialogue.nodes.find(node => node.id === rootNodeId);
    if (!rootNode) {
        errors.push({
            nodeId: 'dialogue',
            message: 'Root node not found in dialogue nodes',
            type: 'error'
        });
        return;
    }
    // Check if root node is NPC type
    if (rootNode.nodeType !== 'npc') {
        errors.push({
            nodeId: rootNodeId,
            message: 'Root node must be an NPC Dialogue node',
            type: 'error'
        });
    }
    // Check if there's only one root node (no other nodes with parent = 0)
    const rootLevelNodes = dialogue.nodes.filter(node => {
        // A node is at root level if it's not referenced as nextDialogueId by any response
        const isReferencedByResponse = dialogue.nodes.some(n => { var _a; return (_a = n.responseOptions) === null || _a === void 0 ? void 0 : _a.some(r => r.nextDialogueId === node.id); });
        return !isReferencedByResponse && node.id !== rootNodeId;
    });
    if (rootLevelNodes.length > 0) {
        rootLevelNodes.forEach(node => {
            errors.push({
                nodeId: node.id,
                message: 'Only one root node is allowed. This node appears to be disconnected.',
                type: 'error'
            });
        });
    }
}
/**
 * Validates an individual NPC node
 */
function validateNPCNode(node, errors, _warnings) {
    // 4. Check if node has text
    if (!node.text || node.text.trim().length === 0) {
        errors.push({
            nodeId: node.id,
            field: 'text',
            message: 'NPC node must have text content',
            type: 'error'
        });
    }
    // 5. Check if NPC is selected
    if (!node.selectedNPCId) {
        errors.push({
            nodeId: node.id,
            field: 'selectedNPCId',
            message: 'NPC character must be selected',
            type: 'error'
        });
    }
    // 3. Check if NPC has at least one response option
    if (!node.responseOptions || node.responseOptions.length === 0) {
        errors.push({
            nodeId: node.id,
            field: 'responseOptions',
            message: 'NPC node must have at least one response option',
            type: 'error'
        });
    }
}
/**
 * Validates a response option
 */
function validateResponseOption(response, parentNodeId, errors, warnings) {
    // 4. Check if response has text
    if (!response.text || response.text.trim().length === 0) {
        errors.push({
            nodeId: `${parentNodeId}_response_${response.id}`,
            field: 'text',
            message: 'Response option must have text content',
            type: 'error'
        });
    }
    // Validate characteristic and value relationship
    if (response.requiredCharacteristic && !response.requiredCharacteristicValue) {
        errors.push({
            nodeId: `${parentNodeId}_response_${response.id}`,
            field: 'requiredCharacteristicValue',
            message: 'Required value must be specified when characteristic is selected',
            type: 'error'
        });
    }
    if (response.requiredCharacteristicValue && !response.requiredCharacteristic) {
        errors.push({
            nodeId: `${parentNodeId}_response_${response.id}`,
            field: 'requiredCharacteristic',
            message: 'Required characteristic must be selected when value is specified',
            type: 'error'
        });
    }
    // Warning for missing reward level when characteristic is set
    if ((response.requiredCharacteristic || response.requiredCharacteristicValue) &&
        (!response.rewardLevel || response.rewardLevel === DialogueRewardLevel.None)) {
        warnings.push({
            nodeId: `${parentNodeId}_response_${response.id}`,
            field: 'rewardLevel',
            message: 'Reward level is recommended when characteristic is required',
            type: 'warning'
        });
    }
}
/**
 * Gets validation errors for tree data structure (used in DialogueEditor)
 */
export function validateTreeData(treeData) {
    const errors = [];
    const warnings = [];
    // Check for root nodes (nodes with parent = 0)
    const rootNodes = treeData.filter(node => node.parent === 0);
    if (rootNodes.length === 0) {
        errors.push({
            nodeId: 'tree',
            message: 'Tree must have at least one root node',
            type: 'error'
        });
    }
    else if (rootNodes.length > 1) {
        rootNodes.forEach((node, index) => {
            if (index > 0) { // Allow first root node, mark others as errors
                errors.push({
                    nodeId: String(node.id),
                    message: 'Only one root node is allowed',
                    type: 'error'
                });
            }
        });
    }
    // Validate each tree node
    treeData.forEach(treeNode => {
        var _a, _b;
        if (((_a = treeNode.data) === null || _a === void 0 ? void 0 : _a.type) === 'dialogue') {
            validateTreeNPCNode(treeNode, treeData, errors, warnings);
        }
        else if (((_b = treeNode.data) === null || _b === void 0 ? void 0 : _b.type) === 'response') {
            validateTreeResponseNode(treeNode, treeData, errors, warnings);
            // Check if response is properly connected to an NPC node
            validateResponseConnection(treeNode, treeData, errors);
        }
    });
    // Detect cycles after basic validation
    detectCyclesInTree(treeData, errors);
    return {
        isValid: errors.length === 0,
        errors,
        warnings
    };
}
function validateTreeNPCNode(treeNode, allNodes, errors, _warnings) {
    var _a;
    const nodeData = treeNode.data.nodeData;
    // Check text
    if (!nodeData.text || nodeData.text.trim().length === 0) {
        errors.push({
            nodeId: String(treeNode.id),
            field: 'text',
            message: 'NPC node must have text content',
            type: 'error'
        });
    }
    // Check NPC selection
    if (!nodeData.selectedNPCId) {
        errors.push({
            nodeId: String(treeNode.id),
            field: 'selectedNPCId',
            message: 'NPC character must be selected',
            type: 'error'
        });
    }
    // Check if has response children
    const hasResponseChildren = allNodes.some(node => { var _a; return node.parent === treeNode.id && ((_a = node.data) === null || _a === void 0 ? void 0 : _a.type) === 'response'; });
    if (!hasResponseChildren) {
        errors.push({
            nodeId: String(treeNode.id),
            field: 'responseOptions',
            message: 'NPC node must have at least one response option',
            type: 'error'
        });
    }
    // --- NEW RULE 1 ---
    // NPC node cannot have another NPC node as a parent (except for root level).
    if (treeNode.parent !== 0) {
        const parentNode = allNodes.find(node => node.id === treeNode.parent);
        if (!parentNode || ((_a = parentNode.data) === null || _a === void 0 ? void 0 : _a.type) !== 'response') {
            errors.push({
                nodeId: String(treeNode.id),
                message: 'NPC node must be a child of a Player Response (cannot follow another NPC node directly)',
                type: 'error'
            });
        }
    }
}
function validateTreeResponseNode(treeNode, allNodes, errors, warnings) {
    var _a;
    const nodeData = treeNode.data.nodeData;
    // Check text
    if (!nodeData.text || nodeData.text.trim().length === 0) {
        errors.push({
            nodeId: String(treeNode.id),
            field: 'text',
            message: 'Response option must have text content',
            type: 'error'
        });
    }
    // Validate characteristic and value relationship
    if (nodeData.requiredCharacteristic && !nodeData.requiredCharacteristicValue) {
        errors.push({
            nodeId: String(treeNode.id),
            field: 'requiredCharacteristicValue',
            message: 'Required value must be specified when characteristic is selected',
            type: 'error'
        });
    }
    if (nodeData.requiredCharacteristicValue && !nodeData.requiredCharacteristic) {
        errors.push({
            nodeId: String(treeNode.id),
            field: 'requiredCharacteristic',
            message: 'Required characteristic must be selected when value is specified',
            type: 'error'
        });
    }
    // Warning for missing reward level
    if ((nodeData.requiredCharacteristic || nodeData.requiredCharacteristicValue) &&
        (!nodeData.rewardLevel || nodeData.rewardLevel === DialogueRewardLevel.None)) {
        warnings.push({
            nodeId: String(treeNode.id),
            field: 'rewardLevel',
            message: 'Reward level is recommended when characteristic is required',
            type: 'warning'
        });
    }
    // --- NEW RULES 2 & 3 (link validation) ---
    const linkedId = nodeData.linkedToDialogueId;
    if (linkedId) {
        // Collect all ancestor NPC dialogue ids
        const ancestorNpcDialogueIds = [];
        let currentParentId = treeNode.parent;
        while (currentParentId !== 0) {
            const parent = allNodes.find(n => n.id === currentParentId);
            if (!parent)
                break;
            if (((_a = parent.data) === null || _a === void 0 ? void 0 : _a.type) === 'dialogue') {
                ancestorNpcDialogueIds.push(parent.data.nodeData.id);
            }
            currentParentId = parent.parent;
        }
        if (ancestorNpcDialogueIds.includes(linkedId)) {
            errors.push({
                nodeId: String(treeNode.id),
                message: 'Link cannot reference an ancestor or parent NPC node (would create cyclic hierarchy)',
                type: 'error'
            });
        }
    }
}
/**
 * Validates response connection in dialogue structure
 */
function validateResponseInDialogue(response, parentNode, dialogue, errors) {
    // Response should always be connected to an NPC node (which is already guaranteed by structure)
    // But we can check if the response leads to a valid next dialogue
    if (response.nextDialogueId) {
        const nextNode = dialogue.nodes.find(node => node.id === response.nextDialogueId);
        if (!nextNode) {
            errors.push({
                nodeId: `${parentNode.id}_response_${response.id}`,
                field: 'nextDialogueId',
                message: 'Response references a non-existent dialogue node',
                type: 'error'
            });
        }
        else if (nextNode.nodeType !== 'npc') {
            errors.push({
                nodeId: `${parentNode.id}_response_${response.id}`,
                field: 'nextDialogueId',
                message: 'Response can only lead to NPC dialogue nodes',
                type: 'error'
            });
        }
    }
}
/**
 * Validates that response nodes are properly connected to NPC nodes
 */
function validateResponseConnection(responseNode, allNodes, errors) {
    var _a;
    // Response cannot be a root node (parent = 0)
    if (responseNode.parent === 0) {
        errors.push({
            nodeId: String(responseNode.id),
            message: 'Response option must be connected to an NPC node',
            type: 'error'
        });
        return;
    }
    // Find parent node
    const parentNode = allNodes.find(node => node.id === responseNode.parent);
    if (!parentNode) {
        errors.push({
            nodeId: String(responseNode.id),
            message: 'Response option has invalid parent reference',
            type: 'error'
        });
        return;
    }
    // Parent must be an NPC dialogue node  
    if (((_a = parentNode.data) === null || _a === void 0 ? void 0 : _a.type) !== 'dialogue') {
        errors.push({
            nodeId: String(responseNode.id),
            message: 'Response option must be directly connected to an NPC Dialogue node',
            type: 'error'
        });
    }
}
/**
 * Detect cycles inside treeData (nested or link)
 */
function detectCyclesInTree(treeData, errors) {
    // Build map dialogueId -> children dialogueIds (via nested or link)
    const childrenMap = new Map();
    treeData.forEach(node => {
        var _a;
        if (((_a = node.data) === null || _a === void 0 ? void 0 : _a.type) !== 'response')
            return;
        const respData = node.data.nodeData;
        const parentNpcId = respData.parentNpcNodeId;
        const targetNpcId = respData.linkedToDialogueId || respData.nextDialogueId;
        if (!targetNpcId)
            return;
        if (!childrenMap.has(parentNpcId))
            childrenMap.set(parentNpcId, []);
        childrenMap.get(parentNpcId).push(targetNpcId);
    });
    const visited = new Set();
    const stack = new Set();
    const dfs = (nodeId) => {
        if (stack.has(nodeId))
            return true;
        if (visited.has(nodeId))
            return false;
        visited.add(nodeId);
        stack.add(nodeId);
        const children = childrenMap.get(nodeId) || [];
        for (const child of children) {
            if (dfs(child))
                return true;
        }
        stack.delete(nodeId);
        return false;
    };
    // Collect all npc ids present
    const npcIds = treeData.filter(n => { var _a; return ((_a = n.data) === null || _a === void 0 ? void 0 : _a.type) === 'dialogue'; }).map(n => n.data.nodeData.id);
    let hasCycle = false;
    npcIds.forEach(id => { if (dfs(id))
        hasCycle = true; });
    if (!hasCycle)
        return;
    // mark responses causing cycle
    treeData.forEach(node => {
        var _a;
        if (((_a = node.data) === null || _a === void 0 ? void 0 : _a.type) !== 'response')
            return;
        const respData = node.data.nodeData;
        const parentNpcId = respData.parentNpcNodeId;
        const targetNpcId = respData.linkedToDialogueId || respData.nextDialogueId;
        if (!targetNpcId)
            return;
        // simple heuristic: cycle if target equals parent or ancestor according to childrenMap traversal
        const isAncestor = (src, target) => {
            let current = src;
            const seen = new Set();
            while (childrenMap.has(current)) {
                const firstChild = childrenMap.get(current)[0];
                if (!firstChild || seen.has(firstChild))
                    break;
                if (firstChild === target)
                    return true;
                seen.add(firstChild);
                current = firstChild;
            }
            return false;
        };
        if (targetNpcId === parentNpcId || isAncestor(parentNpcId, targetNpcId)) {
            // Avoid duplication with the "Link cannot reference an ancestor..." check
            const duplicate = errors.some(e => e.nodeId === String(node.id) && e.message.startsWith('Link cannot reference'));
            if (!duplicate) {
                errors.push({
                    nodeId: String(node.id),
                    message: 'Response creates cyclic dialogue path',
                    type: 'error'
                });
            }
        }
    });
}
